import { db, storage } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth } from 'firebase/auth';

interface FaviconData {
  url: string;
  type: string;
  updatedAt: string;
}

class FaviconService {
  private readonly COLLECTION = 'settings';
  private readonly DOC_ID = 'favicon';
  private readonly STORAGE_PATH = 'favicons';

  async getFavicon(): Promise<FaviconData | null> {
    try {
      const docRef = doc(db, this.COLLECTION, this.DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      return docSnap.data() as FaviconData;
    } catch (error) {
      console.error('Error fetching favicon:', error);
      return null;
    }
  }

  async updateFavicon(file: File): Promise<void> {
    try {
      // Check authentication
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('You must be logged in to update the favicon');
      }

      // Validate file type
      const allowedTypes = ['image/x-icon', 'image/png', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Please upload an .ico, .png, or .svg file.');
      }

      // Create a unique filename
      const timestamp = Date.now();
      const filename = `favicon_${timestamp}${this.getFileExtension(file.type)}`;
      const storageRef = ref(storage, `${this.STORAGE_PATH}/${filename}`);

      // Upload the file
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      // Delete old favicon if it exists
      const oldData = await this.getFavicon();
      if (oldData?.url) {
        try {
          const oldRef = ref(storage, oldData.url);
          await deleteObject(oldRef);
        } catch (error) {
          console.warn('Error deleting old favicon:', error);
        }
      }

      // Update Firestore document
      const data: FaviconData = {
        url,
        type: file.type,
        updatedAt: new Date().toISOString()
      };

      const docRef = doc(db, this.COLLECTION, this.DOC_ID);
      await setDoc(docRef, data);

      // Update favicon in the DOM
      this.updateFaviconInDOM(url, file.type);
    } catch (error) {
      console.error('Error updating favicon:', error);
      throw error;
    }
  }

  private getFileExtension(mimeType: string): string {
    switch (mimeType) {
      case 'image/x-icon':
        return '.ico';
      case 'image/png':
        return '.png';
      case 'image/svg+xml':
        return '.svg';
      default:
        return '';
    }
  }

  private updateFaviconInDOM(url: string, type: string): void {
    const existingFavicon = document.querySelector("link[rel*='icon']");
    
    if (existingFavicon) {
      existingFavicon.setAttribute('href', url);
      existingFavicon.setAttribute('type', type);
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = url;
      link.type = type;
      document.head.appendChild(link);
    }
  }
}

export const faviconService = new FaviconService(); 